/**
 * QrisPaymentModal — Indonesia-only QRIS payment flow with backend tracking.
 *
 * Flow:
 *   1. Buyer fills name + email (+ optional phone) → POST /api/qris/intent
 *   2. Modal shows the QR + EXACT IDR amount.
 *   3. On "I have paid" → POST /api/qris/{ref}/claim-paid → fires admin
 *      email + WhatsApp + buyer confirmation email.
 *   4. Admin verifies in Mandiri/QRIS dashboard and confirms via Admin → Orders.
 */
import { useEffect, useState } from "react";
import { X, Copy, Check, Download, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getMetaAttribution } from "../lib/metaAttribution";
import TX from "./TX";

const formatIdr = (amount) => {
  const n = Math.round(Number(amount) || 0);
  return `Rp ${n.toLocaleString("id-ID")}`;
};

const QrisPaymentModal = ({
  open,
  onClose,
  amountIdr,           // EXACT IDR amount the buyer must transfer
  gemstoneName,        // shown in the modal header for context
  gemstoneId,          // optional — passed through to backend intent
  reference: refProp,  // optional — short order reference (e.g. shortId)
  onConfirmPaid,       // optional callback after backend confirms
}) => {
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState("form"); // form | pay
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [reference, setReference] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset when modal re-opens
      setStep("form");
      setBuyer({ name: "", email: "", phone: "" });
      setReference(null);
      setSubmitting(false);
      setPaying(false);
    }
  }, [open]);

  if (!open) return null;

  const amountText = String(Math.round(Number(amountIdr) || 0));

  const copyAmount = async () => {
    try {
      await navigator.clipboard.writeText(amountText);
      setCopied(true);
      toast.success("Amount copied — paste it in your banking app");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Long-press the amount to select it.");
    }
  };

  const beginPayment = async (e) => {
    e?.preventDefault?.();
    if (!buyer.name.trim() || !buyer.email.trim()) {
      toast.error("Please enter your name and email so we can confirm your order.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/qris/intent", {
        buyer_name: buyer.name.trim(),
        buyer_email: buyer.email.trim(),
        buyer_phone: buyer.phone.trim() || undefined,
        gemstone_id: gemstoneId || undefined,
        gemstone_name: gemstoneName || "Gemstone",
        amount_idr: Number(amountIdr) || 0,
        reference: refProp ? String(refProp).toUpperCase() : undefined,
        ...getMetaAttribution(),
      });
      setReference(data.reference);
      setStep("pay");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPaid = async () => {
    if (!reference) return;
    setPaying(true);
    try {
      await api.post(`/qris/${reference}/claim-paid`);
      toast.success(
        `Thank you! Reference ${reference} — we will verify your QRIS payment and confirm by email within 1 business hour.`
      );
      onConfirmPaid?.(reference);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log your payment. Please email concierge@opikjewels.com with your payment proof.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div
      data-testid="qris-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-onyx border border-gold/30 max-h-[92vh] overflow-y-auto"
      >
        <button
          data-testid="qris-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="px-7 pt-7 pb-2">
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
            <TX>QRIS Payment</TX>
          </div>
          <div className="font-heading text-2xl text-ivory leading-tight">
            {gemstoneName || <TX>Confirm your order</TX>}
          </div>
        </div>

        {step === "form" && (
          <form onSubmit={beginPayment} className="px-7 py-5 space-y-4">
            <p className="text-white/60 text-sm">
              <TX>Enter your contact details to receive your order reference. We will confirm your payment by email within 1 business hour.</TX>
            </p>
            <div>
              <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
                <TX>Full name</TX>
              </label>
              <input
                data-testid="qris-buyer-name"
                type="text"
                required
                autoFocus
                value={buyer.name}
                onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
                className="mt-1 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
              />
            </div>
            <div>
              <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
                <TX>Email</TX>
              </label>
              <input
                data-testid="qris-buyer-email"
                type="email"
                required
                value={buyer.email}
                onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
                className="mt-1 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
              />
            </div>
            <div>
              <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
                <TX>WhatsApp (optional)</TX>
              </label>
              <input
                data-testid="qris-buyer-phone"
                type="tel"
                value={buyer.phone}
                placeholder="+62…"
                onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))}
                className="mt-1 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
              />
            </div>

            <div className="bg-gold/10 border border-gold/30 p-3 text-xs text-ivory">
              <TX>Amount to pay:</TX>{" "}
              <span className="font-heading text-base text-gold">
                {formatIdr(amountIdr)}
              </span>
            </div>

            <button
              type="submit"
              data-testid="qris-continue-btn"
              disabled={submitting}
              className="w-full bg-gold text-black font-accent uppercase tracking-[0.24em] text-[10px] py-3 hover:bg-ivory transition-colors disabled:opacity-50"
            >
              {submitting ? <TX>Working…</TX> : <TX>Continue to QR Code</TX>}
            </button>
          </form>
        )}

        {step === "pay" && (
          <div className="px-7 py-5">
            {/* Reference */}
            {reference && (
              <div className="mb-4 flex items-center justify-between bg-black/40 border border-white/10 px-3 py-2 text-xs">
                <span className="text-white/50 font-accent tracking-[0.22em] uppercase">
                  <TX>Reference</TX>
                </span>
                <span className="text-gold font-mono">{reference}</span>
              </div>
            )}

            {/* QR */}
            <div className="bg-ivory p-4 mb-5 flex items-center justify-center">
              <img
                src="/qris/opik-permata-qris.png"
                alt="QRIS — Opik Permata"
                className="max-h-64 w-auto"
              />
            </div>

            {/* Exact amount */}
            <div className="mb-5">
              <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-1.5">
                <TX>EXACT amount to transfer</TX>
              </div>
              <div className="flex items-center justify-between gap-3 bg-black/40 border border-gold/30 px-3 py-2.5">
                <span className="font-heading text-xl text-ivory">
                  {formatIdr(amountIdr)}
                </span>
                <button
                  type="button"
                  data-testid="qris-copy"
                  onClick={copyAmount}
                  className="flex items-center gap-1.5 font-accent text-[9px] tracking-[0.24em] uppercase text-gold hover:text-ivory"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? <TX>Copied</TX> : <TX>Copy</TX>}
                </button>
              </div>
            </div>

            <ol className="space-y-2.5 mb-5 text-xs text-white/70 leading-relaxed">
              <li className="flex gap-3">
                <span className="text-gold font-accent text-[10px] tracking-[0.18em] mt-0.5">01</span>
                <span><TX>Open any Indonesian banking or e-wallet app (BCA, Mandiri, BRI, GoPay, OVO, Dana, ShopeePay) and tap "Scan QRIS".</TX></span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold font-accent text-[10px] tracking-[0.18em] mt-0.5">02</span>
                <span><TX>Scan the QR code above (or save it and upload).</TX></span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold font-accent text-[10px] tracking-[0.18em] mt-0.5">03</span>
                <span><TX>Enter the EXACT amount shown above and confirm payment to "OPIK PERMATA".</TX></span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold font-accent text-[10px] tracking-[0.18em] mt-0.5">04</span>
                <span><TX>Tap "I have paid" below — a concierge will verify and confirm your order within 1 business hour.</TX></span>
              </li>
            </ol>

            <div className="flex gap-2.5 bg-gold/10 border border-gold/30 p-3 mb-5 text-xs text-ivory leading-relaxed">
              <AlertCircle size={14} className="text-gold flex-shrink-0 mt-0.5" />
              <span>
                <TX>Please transfer the EXACT amount shown above. Sending a different amount will delay verification.</TX>
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <a
                data-testid="qris-download"
                href="/qris/opik-permata-qris.png"
                download="OPIK-PERMATA-QRIS.png"
                className="flex items-center justify-center gap-2 border border-gold/50 text-gold font-accent uppercase tracking-[0.24em] text-[10px] px-4 py-3 hover:bg-gold/10 transition-colors"
              >
                <Download size={13} /> <TX>Save QR</TX>
              </a>
              <button
                type="button"
                data-testid="qris-confirm-paid"
                onClick={confirmPaid}
                disabled={paying}
                className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-[10px] px-4 py-3 hover:bg-ivory transition-colors disabled:opacity-50"
              >
                {paying ? <TX>Sending…</TX> : <TX>I Have Paid</TX>}
              </button>
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 text-center text-white/40 text-[10px] font-accent tracking-[0.22em] uppercase">
              OPIK PERMATA · NMID ID1026488017439
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QrisPaymentModal;
